import { Button, makeStyles, shorthands } from "@fluentui/react-components";
import { Delete20Regular, Add20Regular } from "@fluentui/react-icons";
import { useState } from "react";
import { entryPath, subPath, VERANT_ENTRY_FORMATS } from "@kmvs/forms-core";
import { BRAND } from "./tokens";
import EntryField from "./EntryField";
import KontaktdatenGroup from "./KontaktdatenGroup";
import type { FieldDef, VerantEntry, Kontaktdaten } from "./types";

const useStyles = makeStyles({
  description: {
    display: "block",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.muted,
    marginBottom: "12px",
  },
  addBar: {
    backgroundColor: "#fff",
    ...shorthands.padding("12px"),
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", "#e9e4ee"),
    marginBottom: "16px",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  addBarRow: {
    display: "grid",
    gap: "10px",
    gridTemplateColumns: "1fr",
    "& > *": { minWidth: 0 },
    "@media (min-width: 640px)": {
      gridTemplateColumns: "1fr 1fr auto",
      alignItems: "end",
    },
  },
  addBarLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 600,
    color: "#5d5664",
    marginBottom: "4px",
    display: "block",
  },
  addBarHint: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    color: BRAND.muted,
  },
  card: {
    backgroundColor: "#faf8fb",
    ...shorthands.padding("16px"),
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", "#e9e4ee"),
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
    marginBottom: "12px",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cardTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    fontWeight: 600,
    color: BRAND.ink,
  },
  cardBadge: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    fontWeight: 600,
    color: BRAND.ink,
    backgroundColor: "#fff",
    ...shorthands.padding("3px", "10px"),
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
  },
  cardBadgeTyp: {
    fontSize: "0.74rem",
    fontWeight: 500,
    color: BRAND.muted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  topGrid: {
    display: "grid",
    gap: "10px",
    gridTemplateColumns: "1fr",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  fieldLabel: {
    display: "block",
    minWidth: 0,
  },
  fieldLabelText: {
    display: "block",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#5d5664",
    marginBottom: "3px",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    minWidth: 0,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    ...shorthands.padding("8px", "10px"),
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
  },
  sectionLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
});

const makeId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `ve-${Date.now()}-${Math.floor(Math.random() * 1e6)}`;
};

type Props = {
  field: FieldDef;
  value: VerantEntry[];
  onChange: (next: VerantEntry[]) => void;
};

/**
 * Add-Flow:
 *   1. User waehlt Funktion (Steuern/Recht/Bank/...) und Typ (intern/extern)
 *      in zwei Select-Feldern.
 *   2. Klick auf + Hinzufuegen erzeugt einen neuen Eintrag mit dieser Vor-
 *      belegung und expandiert die passenden Felder (intern: Person + Kontakt,
 *      extern: Firmendaten).
 *   3. Selects werden nach dem Add zurueckgesetzt damit man direkt den
 *      naechsten anlegen kann.
 *
 * Bewusst KEIN Empty-Default-Eintrag: ein leerer Eintrag ohne Funktion ist
 * inhaltlich nutzlos und stiftet nur Verwirrung. Erst wenn der User aktiv
 * auf + klickt, entsteht eine Karte.
 */
const VerantwortlichkeitenList = ({ field, value, onChange }: Props) => {
  const styles = useStyles();
  const options = field.functionOptions ?? [];

  const [pendingFunktion, setPendingFunktion] = useState<string>("");
  const [pendingTyp, setPendingTyp] = useState<"intern" | "extern">("intern");

  const entries = value;

  const update = (id: string, patch: Partial<VerantEntry>) => {
    onChange(entries.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };
  const updateKontakt = (id: string, kontakt: Kontaktdaten) => {
    onChange(entries.map((e) => (e.id === id ? { ...e, kontakt } : e)));
  };
  const remove = (id: string) => {
    onChange(entries.filter((e) => e.id !== id));
  };
  const add = () => {
    if (!pendingFunktion) return;
    const next: VerantEntry = {
      id: makeId(),
      funktion: pendingFunktion,
      typ: pendingTyp,
    };
    onChange([...entries, next]);
    setPendingFunktion("");
    setPendingTyp("intern");
  };

  const funktionLabel = (val: string) =>
    options.find((o) => o.value === val)?.label ?? val;

  return (
    <div>
      {field.description && (
        <span className={styles.description}>{field.description}</span>
      )}

      <div className={styles.addBar}>
        <span className={styles.addBarHint}>
          Funktion und Typ wählen, dann auf <strong>+ Hinzufügen</strong> klicken — danach die Felder ausfüllen.
        </span>
        <div className={styles.addBarRow}>
          <label>
            <span className={styles.addBarLabel}>Funktion / Bereich</span>
            <select
              className={styles.input}
              value={pendingFunktion}
              onChange={(e) => setPendingFunktion(e.target.value)}
            >
              <option value="">— wählen —</option>
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className={styles.addBarLabel}>Typ</span>
            <select
              className={styles.input}
              value={pendingTyp}
              onChange={(e) =>
                setPendingTyp(e.target.value as "intern" | "extern")
              }
            >
              <option value="intern">Intern</option>
              <option value="extern">Extern</option>
            </select>
          </label>
          <Button
            appearance="primary"
            icon={<Add20Regular />}
            onClick={add}
            disabled={!pendingFunktion}
          >
            Hinzufügen
          </Button>
        </div>
      </div>

      {entries.map((entry, idx) => (
        <div key={entry.id} className={styles.card}>
          <div className={styles.cardHeader}>
            <div style={{ display: "flex", alignItems: "center", columnGap: 10, flexWrap: "wrap" }}>
              <span className={styles.cardTitle}>
                {funktionLabel(entry.funktion) || `Zuständigkeit ${idx + 1}`}
              </span>
              <span className={styles.cardBadge}>
                <span className={styles.cardBadgeTyp}>
                  {entry.typ === "extern" ? "Extern" : "Intern"}
                </span>
              </span>
              {entry.uebernommenAlsContactId && (
                <span className={styles.cardBadge} style={{ color: "#1f7a3c" }}>
                  ✓ übernommen
                </span>
              )}
            </div>
            <Button
              appearance="subtle"
              icon={<Delete20Regular />}
              onClick={() => remove(entry.id)}
              aria-label="Eintrag entfernen"
            >
              Entfernen
            </Button>
          </div>

          {/* Person / Ansprechpartner — fuer intern UND extern.
              Bei extern ist die Person der Ansprechpartner der Firma. */}
          <div>
            <span className={styles.sectionLabel}>
              {entry.typ === "extern" ? "Ansprechpartner" : "Person"}
            </span>
            <div className={styles.topGrid} style={{ marginTop: 6 }}>
              <label className={styles.fieldLabel}>
                <span className={styles.fieldLabelText}>Vorname</span>
                <input
                  type="text"
                  className={styles.input}
                  value={entry.vorname ?? ""}
                  onChange={(e) =>
                    update(entry.id, { vorname: e.target.value })
                  }
                />
              </label>
              <label className={styles.fieldLabel}>
                <span className={styles.fieldLabelText}>Nachname</span>
                <input
                  type="text"
                  className={styles.input}
                  value={entry.nachname ?? ""}
                  onChange={(e) =>
                    update(entry.id, { nachname: e.target.value })
                  }
                />
              </label>
            </div>
          </div>
          <div>
            <span className={styles.sectionLabel}>Kontaktdaten</span>
            <div style={{ marginTop: 6 }}>
              <KontaktdatenGroup
                value={entry.kontakt}
                onChange={(k) => updateKontakt(entry.id, k)}
                listKey={field.key}
                pfad={subPath(entryPath(field.key, entry, idx), "kontakt")}
              />
            </div>
          </div>

          {/* Firma — nur bei extern. Wird in der Auswertung als CompanyContact uebernommen. */}
          {entry.typ === "extern" && (
            <div>
              <span className={styles.sectionLabel}>Firma</span>
              <div className={styles.topGrid} style={{ marginTop: 6 }}>
                <label className={styles.fieldLabel}>
                  <span className={styles.fieldLabelText}>Firmenname *</span>
                  <input
                    type="text"
                    className={styles.input}
                    value={entry.firmenname ?? ""}
                    onChange={(e) =>
                      update(entry.id, { firmenname: e.target.value })
                    }
                  />
                </label>
                <label className={styles.fieldLabel}>
                  <span className={styles.fieldLabelText}>Gesellschaftsform</span>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="z. B. GmbH, AG, OHG"
                    value={entry.gesellschaftsform ?? ""}
                    onChange={(e) =>
                      update(entry.id, { gesellschaftsform: e.target.value })
                    }
                  />
                </label>
                <label className={styles.fieldLabel}>
                  <span className={styles.fieldLabelText}>Branche</span>
                  <input
                    type="text"
                    className={styles.input}
                    value={entry.branche ?? ""}
                    onChange={(e) =>
                      update(entry.id, { branche: e.target.value })
                    }
                  />
                </label>
                {/* Web, Telefon und E-Mail werden beim Verlassen geprüft und
                    kanonisiert — die Meldung hängt an DIESER Karte. */}
                <EntryField
                  label="Web"
                  listKey={field.key}
                  pfad={subPath(entryPath(field.key, entry, idx), "web")}
                  format={VERANT_ENTRY_FORMATS.web}
                  placeholder="https://..."
                  value={entry.web ?? ""}
                  onChange={(next) => update(entry.id, { web: next })}
                />
                <EntryField
                  label="Firmen-Telefon"
                  listKey={field.key}
                  pfad={subPath(entryPath(field.key, entry, idx), "telefon")}
                  format={VERANT_ENTRY_FORMATS.telefon}
                  value={entry.telefon ?? ""}
                  onChange={(next) => update(entry.id, { telefon: next })}
                />
                <EntryField
                  label="Firmen-E-Mail"
                  listKey={field.key}
                  pfad={subPath(entryPath(field.key, entry, idx), "email")}
                  format={VERANT_ENTRY_FORMATS.email}
                  value={entry.email ?? ""}
                  onChange={(next) => update(entry.id, { email: next })}
                />
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default VerantwortlichkeitenList;
